import type { FeatureFlags, OpenUrl } from './types';
import type { WebViewMessageEvent } from 'react-native-webview';

interface AnalyticsConfig {
  platform: 'Android' | 'iOS';
  osName: string;
  deviceModel: string;
  appVersion: string;
  adStorage?: 'granted' | 'denied';
  /** Firebase Analytics app instance ID for server-side GA4 MP app-stream forwarding. */
  appInstanceId?: string;
}

const ANALYTICS_CONFIG_CHANGE_EVENT = 'ss:analyticsConfigChanged';

const SKIN_ANALYSIS_ENTRY_KEY = 'skin-analysis-entry';

const BRIDGE_RETRY_DELAY_MS = 250;
const USER_LOGGED_IN_ACK_TIMEOUT_MS = 15_000;

/**
 * How a command reacts when native does not take it (SSD-6564).
 * - `navigate`: the caller falls back to web navigation, so it is never retried. Waiting `sync`
 *   retries are posted first; one that still fails keeps its scheduled retry.
 * - `sync`: state native mirrors. Retried once; sends of the same action during the back-off join
 *   that retry, which carries the latest payload. A send without a payload keeps the waiting one.
 * - `debug`: best effort, neither retried nor reported.
 */
type BridgeDeliveryPolicy = 'navigate' | 'sync' | 'debug';

export type BridgeDeliveryFailureReason = 'no_bridge' | 'no_native_handler' | 'post_failed';

type BridgeDeliveryResult = { delivered: true } | { delivered: false; reason: BridgeDeliveryFailureReason };

/** Telemetry for a command native did not take on the first post. Never carries the payload. */
export interface BridgeDeliveryReport {
  action: string;
  outcome: 'recovered' | 'undelivered' | 'unacknowledged';
  reason: BridgeDeliveryFailureReason | 'ack_timeout';
  attempts: number;
}

export type BridgeDeliveryReporter = (report: BridgeDeliveryReport) => void;

interface BridgeDeliverySettlement {
  delivered: boolean;
  attempts: number;
}

interface PendingSyncRetry {
  payload: unknown;
  reason: BridgeDeliveryFailureReason;
  /** Posts made so far, the first one included. */
  attempts: number;
  onSettled: ReadonlyArray<(settlement: BridgeDeliverySettlement) => void>;
  retryTimer: ReturnType<typeof setTimeout>;
}

type BridgeWindow = Window & {
  ReactNativeWebView?: { postMessage?: (message: string) => void };
  webkit?: { messageHandlers?: Record<string, unknown> };
};

// Same registry pattern as `setServiceLogger`: every bundled copy of this module reports to the one sink.
const DELIVERY_REPORTER_KEY = Symbol.for('@skin-seoul/utils/bridge-delivery-reporter');
const reporterRegistry = globalThis as typeof globalThis & { [DELIVERY_REPORTER_KEY]?: BridgeDeliveryReporter };

function isAnalyticsConfig(value: unknown): value is AnalyticsConfig {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const config = value as Record<string, unknown>;
  return (
    (config.platform === 'Android' || config.platform === 'iOS') &&
    typeof config.osName === 'string' &&
    typeof config.deviceModel === 'string' &&
    typeof config.appVersion === 'string' &&
    (config.adStorage === undefined || config.adStorage === 'granted' || config.adStorage === 'denied') &&
    (config.appInstanceId === undefined || typeof config.appInstanceId === 'string')
  );
}

/**
 * One query value out of a search string, without `URLSearchParams`.
 *
 * This module runs in React Native as well as the browser, and RN's `URL` polyfill does not
 * carry a usable `searchParams`. Parsing the string keeps the same answer on both runtimes.
 */
function readQueryParam(search: string, key: string): string | null {
  const query = search.startsWith('?') ? search.slice(1) : search;
  if (!query) {
    return null;
  }

  for (const pair of query.split('&')) {
    if (!pair) {
      continue;
    }
    const separatorIndex = pair.indexOf('=');
    const rawKey = separatorIndex === -1 ? pair : pair.slice(0, separatorIndex);
    const rawValue = separatorIndex === -1 ? '' : pair.slice(separatorIndex + 1);
    try {
      if (decodeURIComponent(rawKey.replace(/\+/g, ' ')) === key) {
        return decodeURIComponent(rawValue.replace(/\+/g, ' '));
      }
    } catch {
      // A malformed escape in one pair must not discard the rest of the query.
      if (rawKey === key) {
        return rawValue;
      }
    }
  }

  return null;
}

/**
 * Native screen types that can be navigated to from WebView
 */
export type NativeScreen =
  | { screen: 'ProductDetail'; params: { productSlug: string; attributeSize?: string; attributePack?: string } }
  | { screen: 'Home'; params?: undefined }
  /**
   * The Skin Diary tab. `autoStart` carries the one intent the web cannot act on itself:
   * the app sent the guest to `/login` to start an analysis, so the funnel — which exists
   * only natively — must open once the session lands.
   */
  | { screen: 'SkinAnalysisSplash'; params?: { autoStart?: boolean } };

export type ActorIdPayload = {
  actorId: string;
  sessionId: string;
  sessionTimestamp: number;
};

export interface ReferralOrderCompletedPayload {
  orderId: number;
  couponCodes?: string[];
}

export interface ReferralPromptReadyPayload {
  orderId: number;
  couponCodes?: string[];
  orderConfirmed?: boolean;
  referralCouponUsed?: boolean;
  customerId?: number;
}

export interface ShareTextPayload {
  title: string;
  text: string;
}

export type BridgeApiConstructorType = {
  devDebugConsole: (value: string) => void;
  featureFlags?: FeatureFlags;
  onCookieDump?: (payload: { url?: string; domain: string; cookies: string }) => void | Promise<void>;
  onCartUpdated?: () => void | Promise<void>;
  onUserLoggedIn?: () => void | Promise<void>;
  onUserLoggedOut?: () => void | Promise<void>;
  onScrollDetected?: (scrollY: number) => void | Promise<void>;
  onCountryChange?: (country: string) => void | Promise<void>;
  onCartOpen?: () => void | Promise<void>;
  onNavigateToNativeScreen?: (screenData: NativeScreen) => void | Promise<void>;
  onActorIdResolved?: (payload: ActorIdPayload) => void | Promise<void>;
  onOpenOAuthSession?: (payload: OpenOAuthSessionPayload) => void | Promise<void>;
  onOrderIntroChange?: (isPlaying: boolean) => void | Promise<void>;
  onReferralOrderCompleted?: (payload: ReferralOrderCompletedPayload) => void | Promise<void>;
  onReferralPromptReady?: (payload: ReferralPromptReadyPayload) => void | Promise<void>;
  onAnalyticsDebug?: (payload: AnalyticsDebugPayload) => void | Promise<void>;
  onShareText?: (payload: ShareTextPayload) => void | Promise<void>;
};

/**
 * One analytics event the web raised inside the WebView, forwarded so the native debug overlay can
 * show it. Sent only when the webapp has verbose analytics logging on, which is bound to DEV.
 *
 * Shaped loosely on purpose: this is a display payload, not a contract anything branches on, and it
 * must survive the web and the app shipping on different versions.
 */
export type AnalyticsDebugPayload = {
  eventType?: string;
  eventName?: string;
  params?: Record<string, unknown>;
  platform?: string;
  occurredAt?: number;
};

/**
 * Web asks native to run a `platform=mobile` OAuth hop through the system browser
 * (embedded WebViews are rejected by Google with `disallowed_useragent`). Native
 * owns the `returnTo` (the app's custom scheme, allowlisted BE-side via
 * `OAUTH_APP_SCHEME_FRONT`, SSD-4543); web passes only what native needs to build
 * the authorize URL and resume the WebView once the session resolves.
 */
export type OpenOAuthSessionPayload = {
  /** OAuth provider slug, e.g. `google`. */
  provider: string;
  /** API origin to start the OAuth hop against (e.g. https://dev-api.skin-seoul.com). */
  serverOrigin: string;
  /** Guest cart token to carry through login for cart merge (may be empty). */
  cartToken: string;
  /** https callback page to resume the WebView on; the native side appends the one-time code. */
  callbackUrl: string;
};

/**
 * Capability token in the WebView User-Agent suffix (alongside
 * `GOOGLE_PAY_SUPPORTED`). The webapp checks for it to know the build can handle
 * the `openOAuthSession` bridge action; older builds lack it, so social buttons
 * stay hidden for them.
 */
export const OAUTH_SESSION_SUPPORTED = 'OAUTH_SESSION_SUPPORTED';

/**
 * Android-specific capability token for the fixed system-browser OAuth return.
 * The generic token predates the Android App Link fix, so Android WebViews must
 * also advertise this token before the webapp exposes social login.
 */
export const ANDROID_SOCIAL_LOGIN_SUPPORTED = 'ANDROID_SOCIAL_LOGIN_SUPPORTED';

export const NATIVE_SHARE_SUPPORTED = 'NATIVE_SHARE_SUPPORTED';

export class BridgeApi {
  private devDebugConsole: (value: string) => void;
  private featureFlags: FeatureFlags;
  private onCartUpdated?: () => void | Promise<void>;
  private onUserLoggedIn?: () => void | Promise<void>;
  private onUserLoggedOut?: () => void | Promise<void>;
  private onScrollDetected?: (scrollY: number) => void | Promise<void>;
  private onCountryChange?: (country: string) => void | Promise<void>;
  private onCartOpen?: () => void | Promise<void>;
  private onNavigateToNativeScreen?: (screenData: NativeScreen) => void | Promise<void>;
  private onActorIdResolved?: (payload: ActorIdPayload) => void | Promise<void>;
  private onOpenOAuthSession?: (payload: OpenOAuthSessionPayload) => void | Promise<void>;
  private onOrderIntroChange?: (isPlaying: boolean) => void | Promise<void>;
  private onReferralOrderCompleted?: (payload: ReferralOrderCompletedPayload) => void | Promise<void>;
  private onReferralPromptReady?: (payload: ReferralPromptReadyPayload) => void | Promise<void>;
  private onAnalyticsDebug?: (payload: AnalyticsDebugPayload) => void | Promise<void>;
  private onShareText?: (payload: ShareTextPayload) => void | Promise<void>;

  constructor({
    devDebugConsole,
    featureFlags,
    onCartUpdated,
    onUserLoggedIn,
    onUserLoggedOut,
    onScrollDetected,
    onCountryChange,
    onCartOpen,
    onNavigateToNativeScreen,
    onActorIdResolved,
    onOpenOAuthSession,
    onOrderIntroChange,
    onReferralOrderCompleted,
    onReferralPromptReady,
    onAnalyticsDebug,
    onShareText,
  }: BridgeApiConstructorType) {
    this.devDebugConsole = devDebugConsole;
    this.featureFlags = featureFlags || {
      callingEnabled: false,
    };
    this.onCartUpdated = onCartUpdated;
    this.onUserLoggedIn = onUserLoggedIn;
    this.onUserLoggedOut = onUserLoggedOut;
    this.onScrollDetected = onScrollDetected;
    this.onCountryChange = onCountryChange;
    this.onCartOpen = onCartOpen;
    this.onNavigateToNativeScreen = onNavigateToNativeScreen;
    this.onActorIdResolved = onActorIdResolved;
    this.onOpenOAuthSession = onOpenOAuthSession;
    this.onOrderIntroChange = onOrderIntroChange;
    this.onReferralOrderCompleted = onReferralOrderCompleted;
    this.onReferralPromptReady = onReferralPromptReady;
    this.onAnalyticsDebug = onAnalyticsDebug;
    this.onShareText = onShareText;
  }

  private sendMessageToRN = (action: string, payload?: unknown) => {
    const message = JSON.stringify({ action, payload });
    this.devDebugConsole(`Sending to native: ${message}`);

    const result = BridgeApi.postToNative(action, payload);
    if (!result.delivered) {
      console.warn(`ReactNativeWebView.postMessage failed: ${result.reason}`);
    }
  };

  private static pendingSyncRetries = new Map<string, PendingSyncRetry>();

  /** Registers the host app's delivery telemetry sink. Call without a reporter to remove it. */
  public static setDeliveryReporter(reporter?: BridgeDeliveryReporter): void {
    if (!reporter) {
      delete reporterRegistry[DELIVERY_REPORTER_KEY];
      return;
    }
    reporterRegistry[DELIVERY_REPORTER_KEY] = reporter;
  }

  private static reportDelivery(report: BridgeDeliveryReport): void {
    try {
      reporterRegistry[DELIVERY_REPORTER_KEY]?.(report);
    } catch {
      // Telemetry must never turn a handled delivery failure into a thrown one.
    }
  }

  /**
   * Posts one command and never throws. The `ReactNativeWebView` wrapper outlives the native channel:
   * iOS detaches the message handler when the WebView is torn down or its injected scripts are
   * re-applied, and Android throws once the WebView is destroyed (SSD-6564).
   */
  private static postToNative(action: string, payload?: unknown): BridgeDeliveryResult {
    if (typeof window === 'undefined') {
      return { delivered: false, reason: 'no_bridge' };
    }

    const bridgeWindow = window as BridgeWindow;
    const reactNativeWebView = bridgeWindow.ReactNativeWebView;
    if (!reactNativeWebView || typeof reactNativeWebView.postMessage !== 'function') {
      return { delivered: false, reason: 'no_bridge' };
    }

    const iosMessageHandlers = bridgeWindow.webkit?.messageHandlers;
    if (iosMessageHandlers && !iosMessageHandlers.ReactNativeWebView) {
      return { delivered: false, reason: 'no_native_handler' };
    }

    try {
      reactNativeWebView.postMessage(JSON.stringify({ action, payload }));
      return { delivered: true };
    } catch {
      return { delivered: false, reason: 'post_failed' };
    }
  }

  /** Returns whether native took the command now; a `sync` command may still arrive on its retry. */
  private static sendMessageToRNStatic = (
    action: string,
    payload: unknown,
    policy: BridgeDeliveryPolicy,
    onSettled?: (settlement: BridgeDeliverySettlement) => void,
  ): boolean => {
    if (policy === 'navigate') {
      BridgeApi.flushPendingSyncRetries();
    }

    const pendingRetry = policy === 'sync' ? BridgeApi.pendingSyncRetries.get(action) : undefined;
    if (pendingRetry) {
      BridgeApi.pendingSyncRetries.set(action, {
        ...pendingRetry,
        payload: payload === undefined ? pendingRetry.payload : payload,
        onSettled: onSettled ? [...pendingRetry.onSettled, onSettled] : pendingRetry.onSettled,
      });
      return false;
    }

    const result = BridgeApi.postToNative(action, payload);
    if (result.delivered) {
      onSettled?.({ delivered: true, attempts: 1 });
      return true;
    }

    if (policy === 'sync' && result.reason !== 'no_bridge') {
      BridgeApi.pendingSyncRetries.set(action, {
        payload,
        reason: result.reason,
        attempts: 1,
        onSettled: onSettled ? [onSettled] : [],
        retryTimer: setTimeout(() => BridgeApi.retryPendingSync(action, 'timer'), BRIDGE_RETRY_DELAY_MS),
      });
      return false;
    }

    if (policy === 'navigate' && result.reason !== 'no_bridge') {
      BridgeApi.reportDelivery({ action, outcome: 'undelivered', reason: result.reason, attempts: 1 });
    }
    onSettled?.({ delivered: false, attempts: 1 });
    return false;
  };

  /**
   * Posts a waiting `sync` retry. The timer's post is the last attempt and settles it either way. A
   * flush posts early and settles it only on delivery: the gap may still close before the timer
   * fires, so a failed early post keeps the entry and its timer.
   */
  private static retryPendingSync(action: string, trigger: 'timer' | 'flush'): void {
    const pendingRetry = BridgeApi.pendingSyncRetries.get(action);
    if (!pendingRetry) {
      return;
    }

    const result = BridgeApi.postToNative(action, pendingRetry.payload);
    const attempts = pendingRetry.attempts + 1;
    if (!result.delivered && trigger === 'flush') {
      BridgeApi.pendingSyncRetries.set(action, { ...pendingRetry, attempts });
      return;
    }

    BridgeApi.pendingSyncRetries.delete(action);
    clearTimeout(pendingRetry.retryTimer);
    BridgeApi.reportDelivery(
      result.delivered
        ? { action, outcome: 'recovered', reason: pendingRetry.reason, attempts }
        : { action, outcome: 'undelivered', reason: result.reason, attempts },
    );
    pendingRetry.onSettled.forEach((settle) => settle({ delivered: result.delivered, attempts }));
  }

  /**
   * Posts waiting `sync` retries now, oldest first. Runs ahead of a navigation command, because a
   * native navigation can tear this WebView down before the back-off ends and strand them.
   */
  private static flushPendingSyncRetries(): void {
    [...BridgeApi.pendingSyncRetries.keys()].forEach((action) => BridgeApi.retryPendingSync(action, 'flush'));
  }

  public isFeatureEnabled(feature: keyof FeatureFlags): boolean {
    return this.featureFlags[feature] || false;
  }

  public updateFeatureFlags(flags: Partial<FeatureFlags>): void {
    this.featureFlags = { ...this.featureFlags, ...flags };
  }

  public openUrl(data: OpenUrl): void {
    this.sendMessageToRN('openUrl', data);
  }

  public goBack(): void {
    this.sendMessageToRN('goBack');
  }

  public reload(): void {
    this.sendMessageToRN('reload');
  }

  public static cartUpdated(): void {
    this.sendMessageToRNStatic('cartUpdated', undefined, 'sync');
  }

  public static cartOpen(): void {
    this.sendMessageToRNStatic('cartOpen', undefined, 'sync');
  }

  /** True means the request was dispatched, not that the user completed sharing. */
  public static shareText(payload: ShareTextPayload): boolean {
    if (typeof navigator === 'undefined' || !navigator.userAgent.split(/\s+/).includes(NATIVE_SHARE_SUPPORTED)) {
      return false;
    }
    const webview = this.isWebview();
    if (!webview || typeof webview.postMessage !== 'function') {
      return false;
    }
    return this.sendMessageToRNStatic('shareText', payload, 'navigate');
  }

  public static userLoggedOut(): void {
    this.sendMessageToRNStatic('userLoggedOut', undefined, 'sync');
  }

  /**
   * Notify native that user logged in and wait for native operations to complete
   * Returns a Promise that resolves when native side completes all auth-related operations,
   * or right away when native could not be reached at all. It never rejects. A delivered command
   * that native never acknowledges is reported after the ack timeout and keeps waiting (SSD-6564).
   * Note: Caller must check isWebview() before calling this method
   */
  public static userLoggedIn(): Promise<void> {
    return new Promise((resolve) => {
      const windowWithHandler = window as Window & {
        __ssUserLoggedInComplete?: () => void;
      };
      let ackWatchdog: ReturnType<typeof setTimeout> | undefined;
      let isComplete = false;
      // Calls made while one is still waiting share native's single ack, so it completes them all.
      const completeEarlierCall = windowWithHandler.__ssUserLoggedInComplete;

      const complete = () => {
        if (isComplete) {
          return;
        }
        isComplete = true;
        clearTimeout(ackWatchdog);
        if (windowWithHandler.__ssUserLoggedInComplete === complete) {
          delete windowWithHandler.__ssUserLoggedInComplete;
        }
        completeEarlierCall?.();
        resolve();
      };

      windowWithHandler.__ssUserLoggedInComplete = complete;

      this.sendMessageToRNStatic('userLoggedIn', undefined, 'sync', ({ delivered, attempts }) => {
        if (isComplete) {
          return;
        }
        if (!delivered) {
          complete();
          return;
        }
        ackWatchdog = setTimeout(() => {
          BridgeApi.reportDelivery({
            action: 'userLoggedIn',
            outcome: 'unacknowledged',
            reason: 'ack_timeout',
            attempts,
          });
        }, USER_LOGGED_IN_ACK_TIMEOUT_MS);
      });
    });
  }

  /** `userLoggedIn` for a page that moves on without waiting for native's ack, like the OAuth callback. */
  public static notifyUserLoggedIn(): void {
    this.sendMessageToRNStatic('userLoggedIn', undefined, 'sync');
  }

  /**
   * Hands the social login to native's system browser. Returns whether native took it; never retried,
   * so a late delivery cannot open the browser after the user has moved on.
   */
  public static openOAuthSession(payload: OpenOAuthSessionPayload): boolean {
    return this.sendMessageToRNStatic('openOAuthSession', payload, 'navigate');
  }

  /**
   * Notify native app that actorId has been resolved/changed.
   * Called by PersonalizationAuthSync when actorId changes in WebView.
   * Native side saves the new identity to AsyncStorage + CookieManager.
   *
   * Fire-and-forget — no callback needed (AsyncStorage write is fast).
   */
  /**
   * Mirrors one analytics event to the native debug overlay. Called only while the webapp has
   * verbose analytics logging on; outside a WebView the static sender is a no-op.
   */
  public static analyticsDebug(payload: AnalyticsDebugPayload): void {
    this.sendMessageToRNStatic('analyticsDebug', payload, 'debug');
  }

  public static actorIdResolved(payload: ActorIdPayload): void {
    this.sendMessageToRNStatic('actorIdResolved', payload, 'sync');
  }

  /**
   * Navigate to a native screen from WebView
   * Use this for all native screen navigations
   * Returns false when native did not take the command, so the caller can navigate on the web instead.
   */
  public static navigateToNativeScreen(screenData: NativeScreen): boolean {
    return this.sendMessageToRNStatic('navigateToNativeScreen', screenData, 'navigate');
  }

  /**
   * SSD-5769: the post-payment intro is a full-screen takeover on mobile, but the native shell
   * draws the header above the WebView, which the web page cannot reach. Web announces when the
   * intro starts and ends so native can stand its chrome down for the duration.
   */
  public static orderIntroChange(isPlaying: boolean): void {
    this.sendMessageToRNStatic('orderIntroChange', isPlaying, 'sync');
  }

  public static referralOrderCompleted(orderId: number, couponCodes?: string[]): void {
    if (Number.isSafeInteger(orderId) && orderId > 0) {
      this.sendMessageToRNStatic(
        'referralOrderCompleted',
        { orderId, ...(couponCodes ? { couponCodes } : {}) },
        'sync',
      );
    }
  }

  public static referralPromptReady(
    orderId: number,
    couponCodes?: string[],
    confirmation?: { orderConfirmed?: boolean; referralCouponUsed: boolean; customerId: number },
  ): void {
    if (Number.isSafeInteger(orderId) && orderId > 0) {
      this.sendMessageToRNStatic(
        'referralPromptReady',
        {
          orderId,
          ...(couponCodes !== undefined ? { couponCodes } : {}),
          ...(confirmation ?? {}),
        },
        'sync',
      );
    }
  }

  public static isWebview = () => {
    return (
      typeof window !== 'undefined' &&
      (window as Window & { ReactNativeWebView?: { postMessage: (message: string) => void } }).ReactNativeWebView
    );
  };

  /**
   * Parse URL and return native screen data if it should be handled by native app
   * Returns null if URL should be handled by web
   */
  public static parseUrlToNativeScreen = (url: string): NativeScreen | null => {
    try {
      // Extract pathname from URL - handle both absolute and relative URLs
      let pathname = '/';
      let search = '';

      if (url.startsWith('http://') || url.startsWith('https://')) {
        // Absolute URL - use URL constructor
        const parsed = new URL(url);
        pathname = parsed.pathname || '/';
        search = parsed.search;
      } else if (url.startsWith('/')) {
        // Relative URL starting with /
        const [rawPath, ...queryParts] = url.split('#')[0].split('?');
        const rawSearch = queryParts.join('?');
        pathname = rawPath || '/';
        search = rawSearch ? `?${rawSearch}` : '';
      } else {
        return null;
      }

      const normalizedPath = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : pathname;

      // Home screen
      if (normalizedPath === '/') {
        return { screen: 'Home' };
      }

      // Product detail screen
      if (normalizedPath.startsWith('/product/')) {
        const productSlug = normalizedPath.replace('/product/', '');
        const attributeSize = readQueryParam(search, 'attribute_size');
        const attributePack = readQueryParam(search, 'attribute_pack');

        return {
          screen: 'ProductDetail',
          params: {
            productSlug,
            ...(attributeSize ? { attributeSize } : {}),
            ...(attributePack ? { attributePack } : {}),
          },
        };
      }

      // Skin Diary. The web page exists, but every CTA on it hands off to the app anyway
      // (`useAppHandoff`), and inside the app's own WebView that handoff dead-ends at the store
      // (SSD-5806) — so the native tab is always the right destination here. `start=survey` is
      // set by the app's own login round-trip and asks the funnel to open behind the tab.
      if (normalizedPath === '/skin-diary') {
        const startsSurvey = readQueryParam(search, 'start') === 'survey';
        return startsSurvey
          ? { screen: 'SkinAnalysisSplash', params: { autoStart: true } }
          : { screen: 'SkinAnalysisSplash' };
      }

      // The `/app` launcher opens the app from outside it (SSD-5015). Inside the app's WebView it
      // would only bounce back in through the custom scheme, so go straight to the tab (SSD-7076).
      if (normalizedPath === '/app' && readQueryParam(search, 'key') === SKIN_ANALYSIS_ENTRY_KEY) {
        return { screen: 'SkinAnalysisSplash' };
      }

      return null;
    } catch {
      return null;
    }
  };

  /**
   * Try to navigate to native screen if in WebView and URL matches a native screen
   * Returns true if handled by native, false if should proceed with web navigation,
   * including when native could not take the command (SSD-6564)
   */
  public static tryNavigateToNativeScreen = (href: string): boolean => {
    const screenData = BridgeApi.parseUrlToNativeScreen(href);
    if (screenData) {
      return BridgeApi.navigateToNativeScreen(screenData);
    }
    return false;
  };

  public async handleNativeMessage(message: WebViewMessageEvent): Promise<void> {
    try {
      const data = JSON.parse(message.nativeEvent.data);
      const { action, payload } = data;

      switch (action) {
        case 'shareText': {
          if (
            payload &&
            typeof payload === 'object' &&
            typeof payload.title === 'string' &&
            typeof payload.text === 'string' &&
            payload.text.trim().length > 0
          ) {
            await this.onShareText?.({ title: payload.title, text: payload.text });
          }
          break;
        }
        case 'cartUpdated': {
          try {
            if (this.onCartUpdated) {
              void this.onCartUpdated();
            }
          } catch (e) {
            console.warn('onCartUpdated handler threw', e);
          }
          break;
        }

        case 'userLoggedIn': {
          try {
            if (this.onUserLoggedIn) {
              await this.onUserLoggedIn();
            }
          } catch (e) {
            console.warn('onUserLoggedIn handler threw', e);
          }
          break;
        }

        case 'userLoggedOut': {
          try {
            if (this.onUserLoggedOut) {
              void this.onUserLoggedOut();
            }
          } catch (e) {
            console.warn('onUserLoggedOut handler threw', e);
          }
          break;
        }

        case 'error':
          const errorMessage =
            payload && typeof payload === 'object' && 'message' in payload
              ? (payload as { message: string }).message
              : 'Unknown error';
          console.error('Bridge error:', errorMessage);
          break;
        case 'countryChange': {
          try {
            if (this.onCountryChange) {
              void this.onCountryChange(payload as string);
            }
          } catch (e) {
            console.warn('onCountryChange handler threw', e);
          }
          break;
        }

        case 'scroll': {
          try {
            if (this.onScrollDetected) {
              void this.onScrollDetected(payload as number);
            }
          } catch (e) {
            console.warn('onScrollDetected handler threw', e);
          }
          break;
        }
        case 'cartOpen': {
          try {
            if (this.onCartOpen) {
              void this.onCartOpen();
            }
          } catch (e) {
            console.warn('onCartOpen handler threw', e);
          }
          break;
        }

        case 'referralOrderCompleted': {
          if (payload && typeof payload === 'object' && Number.isSafeInteger(payload.orderId) && payload.orderId > 0) {
            const couponCodes =
              Array.isArray(payload.couponCodes) &&
              payload.couponCodes.every((code: unknown) => typeof code === 'string')
                ? payload.couponCodes
                : undefined;
            await this.onReferralOrderCompleted?.({
              orderId: payload.orderId,
              ...(couponCodes ? { couponCodes } : {}),
            });
          }
          break;
        }

        case 'referralPromptReady': {
          if (payload && typeof payload === 'object' && Number.isSafeInteger(payload.orderId) && payload.orderId > 0) {
            const couponCodes =
              Array.isArray(payload.couponCodes) &&
              payload.couponCodes.every((code: unknown) => typeof code === 'string')
                ? payload.couponCodes
                : undefined;
            await this.onReferralPromptReady?.({
              orderId: payload.orderId,
              ...(couponCodes !== undefined ? { couponCodes } : {}),
              ...((payload.orderConfirmed === true || payload.referralCouponUsed === true) &&
              Number.isSafeInteger(payload.customerId) &&
              payload.customerId >= 0
                ? {
                    customerId: payload.customerId,
                    ...(payload.orderConfirmed === true ? { orderConfirmed: true } : {}),
                    ...(typeof payload.referralCouponUsed === 'boolean'
                      ? { referralCouponUsed: payload.referralCouponUsed }
                      : {}),
                  }
                : {}),
            });
          }
          break;
        }

        case 'orderIntroChange': {
          try {
            if (this.onOrderIntroChange) {
              void this.onOrderIntroChange(Boolean(payload));
            }
          } catch (e) {
            console.warn('onOrderIntroChange handler threw', e);
          }
          break;
        }

        case 'navigateToNativeScreen': {
          try {
            if (this.onNavigateToNativeScreen) {
              void this.onNavigateToNativeScreen(payload as NativeScreen);
            }
          } catch (e) {
            console.warn('onNavigateToNativeScreen handler threw', e);
          }
          break;
        }

        case 'analyticsDebug': {
          try {
            if (this.onAnalyticsDebug) {
              await this.onAnalyticsDebug(payload as AnalyticsDebugPayload);
            }
          } catch (e) {
            console.warn('onAnalyticsDebug handler threw', e);
          }
          break;
        }

        case 'actorIdResolved': {
          try {
            if (this.onActorIdResolved) {
              await this.onActorIdResolved(payload as ActorIdPayload);
            }
          } catch (e) {
            console.warn('onActorIdResolved handler threw', e);
          }
          break;
        }

        case 'openOAuthSession': {
          try {
            if (this.onOpenOAuthSession) {
              // Fire-and-forget: the OAuth round-trip far outlives any message
              // timeout; the native handler resumes the WebView on the callback.
              void this.onOpenOAuthSession(payload as OpenOAuthSessionPayload);
            }
          } catch (e) {
            console.warn('onOpenOAuthSession handler threw', e);
          }
          break;
        }

        default:
          this.devDebugConsole(`Unknown message action: ${action}`);
      }
    } catch (error) {
      // Silently ignore parsing errors for non-bridge messages
      this.devDebugConsole(`Failed to parse message: ${message.nativeEvent.data}, error: ${error}`);
    }
  }

  // Utilities for cookie handling
  public static parseCookieString(cookieString: string): Array<{ name: string; value: string }> {
    return cookieString
      .split(';')
      .map((c) => c.trim())
      .filter(Boolean)
      .reduce<Array<{ name: string; value: string }>>((acc, pair) => {
        const eqIndex = pair.indexOf('=');
        if (eqIndex > -1) {
          const name = pair.slice(0, eqIndex).trim();
          const value = pair.slice(eqIndex + 1).trim();
          if (name) {
            acc.push({ name, value });
          }
        }
        return acc;
      }, []);
  }

  public static regionCookieInjection(params: { country: string; countryKey: string }): string {
    const { country, countryKey } = params;
    const lines: string[] = [];
    if (country) {
      const encoded = encodeURIComponent(country);
      lines.push(
        `console.log('[Bridge] Setting ${countryKey}=${country}');`,
        `document.cookie = '${countryKey}='+decodeURIComponent('${encoded}')+'; path=/; max-age=31536000';`,
      );
    }
    return lines.length > 0 ? lines.join('\n') : `// no region cookies to set`;
  }

  public static cartSessionCookieInjection(params: { name: string; value: string }): string {
    const { name, value } = params;
    return `
      document.cookie = '${name}=${value}; path=/; max-age=31536000';
    `;
  }

  // Expose a global function for web to call: window.__ssCartUpdated(payload)
  public static exposeCartUpdatedGlobal(): string {
    return `
      try {
        window.__ssCartUpdated = function () {
          try {
            const message = { action: 'cartUpdated' };
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('cartUpdated post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  // Expose a global function for web to call: window.__ssUserLoggedOut()
  public static exposeUserLoggedOutGlobal(): string {
    return `
      try {
        window.__ssUserLoggedOut = function () {
          try {
            const message = { action: 'userLoggedOut' };
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('userLoggedOut post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  /**
   * Expose a global function for native to inject actorId into WebView cookies.
   * Dispatches a CustomEvent so the web PersonalizationClient can react.
   *
   * @param storageKeys - Cookie key names for personalization identity.
   *   Pass PERSONALIZATION_STORAGE_KEYS from personalization/core/constants.
   */
  public static exposeActorIdReceiverGlobal(storageKeys: {
    ACTOR_ID: string;
    SESSION_ID: string;
    SESSION_TS: string;
  }): string {
    return `
      try {
        window.__ssSetActorId = function(opts) {
          try {
            document.cookie = '${storageKeys.ACTOR_ID}=' + encodeURIComponent(opts.actorId) + '; path=/; max-age=31536000; secure; samesite=Lax';
            document.cookie = '${storageKeys.SESSION_ID}=' + encodeURIComponent(opts.sessionId) + '; path=/; secure; samesite=Lax';
            document.cookie = '${storageKeys.SESSION_TS}=' + String(opts.sessionTimestamp) + '; path=/; secure; samesite=Lax';
            window.dispatchEvent(new CustomEvent('ss:actorIdInjected', {
              detail: opts
            }));
          } catch (e) {}
        };
      } catch (_) {}
    `;
  }

  /**
   * Expose analytics platform config to WebView.
   * WebView's WebAnalyticsPlatformProvider reads these via BridgeApi.getAnalyticsConfig().
   */
  public static exposeAnalyticsConfigGlobal(params: AnalyticsConfig): string {
    return `
      try {
        window.__ssAnalyticsConfig = ${JSON.stringify(params)};
        window.dispatchEvent(new Event('${ANALYTICS_CONFIG_CHANGE_EVENT}'));
      } catch (_) {}
    `;
  }

  /**
   * Read analytics config injected by native app.
   * Returns config if running inside WebView, undefined otherwise.
   */
  public static getAnalyticsConfig(): AnalyticsConfig | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const config = (window as Window & { __ssAnalyticsConfig?: unknown }).__ssAnalyticsConfig;
    if (!isAnalyticsConfig(config)) {
      return undefined;
    }
    return config;
  }

  public static subscribeToAnalyticsConfigChange(listener: () => void): () => void {
    if (typeof window === 'undefined') {
      return () => undefined;
    }
    window.addEventListener(ANALYTICS_CONFIG_CHANGE_EVENT, listener);
    // Replay the current snapshot after subscribing so an injection between render and effect is not lost.
    listener();
    return () => window.removeEventListener(ANALYTICS_CONFIG_CHANGE_EVENT, listener);
  }

  public static exposeCountryChangeGlobal(): string {
    return `
      try {
        window.__ssCountryChange = function (country) {
          try {
            const message = { action: 'countryChange', payload: country };
  
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify(message));
            }
          } catch (e) {
            console.log('countryChange post failed', e);
          }
        };
      } catch (_) {}
    `;
  }

  // Add scroll event listener for header animation
  public static detectScroll(): string {
    return `
      let lastScrollY = 0;
      const throttle = (func, delay) => {
        let timeoutId;
        let lastExecTime = 0;
        return function (...args) {
          const currentTime = Date.now();
          if (currentTime - lastExecTime > delay) {
            func.apply(this, args);
            lastExecTime = currentTime;
          } else {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
              func.apply(this, args);
              lastExecTime = Date.now();
            }, delay - (currentTime - lastExecTime));
          }
        };
      };
      
      const handleScroll = throttle(() => {
        const scrollY = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (Math.abs(scrollY - lastScrollY) > 1) { // Only send if scroll changed significantly
          try {
            if (window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
              window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'scroll', payload: scrollY }));
            }
            lastScrollY = scrollY;
          } catch (e) {
            console.log('scroll detection post failed', e);
          }
        }
      }, 16); // 60fps throttling
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      document.addEventListener('scroll', handleScroll, { passive: true });
    `;
  }

  // Handle GDPR cookie banner based on ATT tracking permission status
  //TODO: find a way to handle this instead of using a long string function
  // https://github.com/SkinSeoul/SkinSeoulFront/pull/633#discussion_r2501866352
  public static handleGDPRBanner(params: { trackingStatus: string | null }): string {
    const { trackingStatus } = params;
    return `(function handleGDPRBanner() {
        const trackingStatus = '${trackingStatus || 'null'}';
        
        if (trackingStatus === 'null') {
          return;
        }
        
        const getCookie = (name) => {
          const value = '; ' + document.cookie;
          const parts = value.split('; ' + name + '=');
          if (parts.length === 2) return parts.pop().split(';').shift();
          return null;
        };
        
        const mooveGdprPopup = getCookie('moove_gdpr_popup');
        if (mooveGdprPopup) {
          return;
        }
        
        let isHandled = false;
        let bannerObserver = null;
        
        const setupBannerObserver = () => {
          const banner = document.getElementById('moove_gdpr_cookie_info_bar');
          if (!banner) {  
            return;
          }
          
          bannerObserver = new MutationObserver((mutations) => {
            if (isHandled) {
              return;
            }
            
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                const newClass = banner.getAttribute('class') || '';
                const isVisible = newClass.indexOf('moove-gdpr-info-bar-hidden') === -1;
                
                if (isVisible && !isHandled) {
                  if (!isHandled) {
                    handleBanner();
                  }
                }
              }
            });
          });
          
          bannerObserver.observe(banner, {
            attributes: true,
            attributeOldValue: false,
            attributeFilter: ['class']
          });
        };
        
        const handleBanner = () => {
          if (isHandled) {
            return;
          }
          
          const banner = document.getElementById('moove_gdpr_cookie_info_bar');
          
          if (!banner || trackingStatus === 'undetermined') {
            return;
          }
          
          isHandled = true;
          
          if (trackingStatus === 'granted') {
            const acceptButton = banner.querySelector('.moove-gdpr-infobar-allow-all');
            if (acceptButton) {
              acceptButton.click();
              banner.style.display = 'none';
            } 
          } else if (trackingStatus === 'denied') {
            const rejectButton = banner.querySelector('.moove-gdpr-infobar-reject-btn');
            if (rejectButton) {
              rejectButton.click();
              banner.style.display = 'none';
            }
          } else {
            banner.style.display = 'none';
          }
          
          if (bannerObserver) {
            bannerObserver.disconnect();
            bannerObserver = null;
          }
        };
        
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            setupBannerObserver();
          });
        } else {
          setupBannerObserver();
        }
      })();
    `;
  }

  /**
   * Detect clicks on WordPress add-to-cart buttons and notify native app
   * Uses event delegation to handle dynamically added elements
   */
  public static detectAddToCartClicks(params: { selector: string }): string {
    const { selector } = params;
    return `
      (function() {
        document.addEventListener('click', function(e) {
          var addToCartButton = e.target.closest('${selector}');
          if (addToCartButton && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
            // Delay to allow WordPress to process add-to-cart
            setTimeout(function() {
              try {
                window.ReactNativeWebView.postMessage(JSON.stringify({ action: 'cartUpdated' }));
              } catch (_) {}
            }, 500);
          }
        }, { passive: true });
      })();
    `;
  }
}
